import React from 'react';
import { Loader2, Mail } from 'lucide-react';

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'All registered users' },
  { value: 'paid', label: 'Paid users only' },
  { value: 'unpaid', label: 'Unpaid users only' },
];

// Shared audience / preview / test-send panel reused by both the "New Announcement"
// create flow and the "Email This Post" flow on an existing announcement.
export const AnnouncementEmailPanel = (props) => {
  const footerPreview =
    'Open Flight Sim Academy: https://app.flightsimacademy.co.uk\n' +
    "You're receiving this because you have a Flight Sim Academy account. Unsubscribe: [link]";

  return (
    <div className="mt-4 p-4 bg-cockpit-bg border border-cockpit-border rounded-sm space-y-4" data-testid="announcement-email-panel">
      {/* Audience */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-cockpit-text-secondary uppercase tracking-wider">Audience</label>
          {props.counts.opted_out > 0 && (
            <span className="text-[10px] text-cockpit-text-secondary/60" data-testid="opted-out-count">
              {props.counts.opted_out} opted out
            </span>
          )}
        </div>
        <div className="flex flex-col gap-2" role="radiogroup" aria-label="Email audience">
          {AUDIENCE_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center justify-between p-2.5 bg-cockpit-panel border border-cockpit-border rounded-sm cursor-pointer hover:border-fsa-blue/50 transition-colors"
              data-testid={`email-audience-option-${opt.value}`}
            >
              <span className="flex items-center gap-2">
                <input
                  type="radio"
                  name="announcement-email-audience"
                  value={opt.value}
                  checked={props.audience === opt.value}
                  onChange={() => props.onAudienceChange(opt.value)}
                  className="accent-fsa-blue"
                  data-testid={`email-audience-radio-${opt.value}`}
                />
                <span className="text-sm text-cockpit-text-primary">{opt.label}</span>
              </span>
              <span
                className="px-2 py-0.5 text-xs font-medium rounded-sm bg-fsa-blue/20 text-fsa-blue flex-shrink-0"
                data-testid={`email-audience-count-${opt.value}`}
              >
                {props.countsLoading ? '…' : props.counts[opt.value]}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Preview */}
      <div>
        <p className="text-[10px] text-cockpit-text-secondary uppercase tracking-wider mb-1">Email Preview</p>
        <div className="p-3 bg-cockpit-panel border border-cockpit-border rounded-sm" data-testid="email-preview">
          <p className="text-sm text-cockpit-text-primary font-medium mb-2">{props.title || '(no title)'}</p>
          <p className="text-sm text-cockpit-text-secondary whitespace-pre-wrap mb-3">{props.content || '(no message)'}</p>
          <p className="text-xs text-cockpit-text-secondary/60 whitespace-pre-wrap border-t border-cockpit-border pt-2">{footerPreview}</p>
        </div>
      </div>

      {/* Sender info + test send */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-[10px] text-cockpit-text-secondary">
          <Mail className="w-3 h-3 inline mr-1" />
          From: <span className="text-cockpit-text-primary font-medium">support@flightsimacademy.co.uk</span>
        </p>
        <button
          type="button"
          onClick={props.onSendTest}
          disabled={props.sendingTest || !props.title.trim() || !props.content.trim()}
          className="px-3 py-1.5 text-xs font-medium rounded-sm bg-cockpit-panel text-cockpit-text-secondary hover:bg-cockpit-border transition-colors disabled:opacity-40"
          data-testid="send-test-email-btn"
        >
          {props.sendingTest ? (
            <span className="flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" />
              Sending...
            </span>
          ) : (
            'Send Test to Me'
          )}
        </button>
      </div>
      {props.testSentTo && (
        <p className="text-xs text-status-success" data-testid="test-sent-confirmation">
          Test email sent to {props.testSentTo}
        </p>
      )}
    </div>
  );
};

export default AnnouncementEmailPanel;
